import { t } from "@lingui/core/macro";
import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
  useAppKitProvider,
  useDisconnect,
} from "@reown/appkit/react";
import type { ReactNode } from "react";
import { WagmiProvider } from "wagmi";
import { NETWORK_CONFIG } from "../utils/config";
import { type EIP1193Provider, sendEvmTransaction } from "../utils/evm-send-transaction";
import {
  type SolanaWalletAdapterLike,
  sendSolanaTransaction,
} from "../utils/solana-send-transaction";
import {
  type TransactionRequest,
  type TransactionResponse,
  type WalletAccount,
  WalletAdapterStatus,
} from "../utils/types";
import {
  initializeAppKit,
  initializeSolanaAdapter,
  initializeWagmiAdapter,
} from "../utils/wallet-initialization";
import { WalletContext, type WalletContextType } from "./shared-wallet-context";

interface InternalWalletProviderProps {
  children: ReactNode;
}

export function InternalWalletProvider({ children }: InternalWalletProviderProps) {
  // Initialize both adapters and AppKit only when this component renders
  // Both are initialized here for consistency, even though only WagmiAdapter is used in the provider
  const wagmiAdapter = initializeWagmiAdapter();
  const solanaAdapter = initializeSolanaAdapter();
  initializeAppKit(wagmiAdapter, solanaAdapter);

  // Wrap children with WagmiProvider to provide Wagmi context for AppKit hooks
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <InternalWalletProviderWithHooks>{children}</InternalWalletProviderWithHooks>
    </WagmiProvider>
  );
}

// Component that uses AppKit hooks (must be inside AppKit context)
function InternalWalletProviderWithHooks({ children }: InternalWalletProviderProps) {
  // AppKit hooks
  const appKit = useAppKit();
  const appKitAccount = useAppKitAccount();
  const appKitNetwork = useAppKitNetwork();
  const { disconnect } = useDisconnect();

  const namespace = appKitNetwork.caipNetwork?.chainNamespace ?? NETWORK_CONFIG.defaultNamespace;
  const chainId = appKitNetwork.chainId ?? NETWORK_CONFIG.defaultChainId;
  // AppKit uses `caipNetworkId` - we map it to our internal `caip2ChainId` property
  const caipNetworkId = appKitNetwork.caipNetworkId ?? NETWORK_CONFIG.defaultChainNetworkId;

  // Get provider for the appropriate namespace
  // Note: walletProvider may be undefined if no wallet is connected for the selected namespace
  const { walletProvider: evmProvider } = useAppKitProvider("eip155");
  const { walletProvider: solanaProvider } = useAppKitProvider("solana");
  const walletProvider = namespace === "solana" ? solanaProvider : evmProvider;

  // Derive state from AppKit
  // Map AppKit's `caipNetworkId` to our internal `caip2ChainId` for consistency
  const selectedAccount: WalletAccount | null = appKitAccount.address
    ? {
        address: appKitAccount.address,
        namespace,
        chainId,
        caip2ChainId: caipNetworkId, // Maps from AppKit's caipNetworkId to our caip2ChainId
        name: t`Wallet`,
      }
    : null;

  const status: WalletAdapterStatus = appKitAccount.isConnected
    ? WalletAdapterStatus.CONNECTED
    : appKitAccount.status === "connecting"
      ? WalletAdapterStatus.CONNECTING
      : WalletAdapterStatus.DISCONNECTED;

  // Connect function
  const connect = async (): Promise<void> => {
    await appKit.open();
  };

  // Send transaction
  const sendTransaction = async (tx: TransactionRequest): Promise<TransactionResponse> => {
    if (!selectedAccount) {
      throw new Error(t`No account selected`);
    }

    // Validate that wallet provider is available before attempting to send transaction
    if (!walletProvider) {
      throw new Error(t`Wallet provider not available. Please ensure your wallet is connected.`);
    }

    // Solana path: delegate to Solana-specific send utility
    if (selectedAccount.namespace === "solana") {
      const solanaWallet = walletProvider as SolanaWalletAdapterLike;

      return sendSolanaTransaction(solanaWallet, selectedAccount, tx);
    }

    // EVM path (existing behavior)
    const provider = walletProvider as EIP1193Provider;
    return sendEvmTransaction(provider, selectedAccount, tx);
  };

  // Switch chain
  const switchChain = async (_supportedChains?: string[]): Promise<boolean> => {
    // For internal mode, always open the network selection modal
    // The modal will show all available networks and the user can choose
    await appKit.open({ view: "Networks" });
    return true;
  };

  // Close modal
  const closeModal = (): void => {
    appKit.close();
  };

  const contextValue: WalletContextType = {
    status,
    selectedAccount,
    connect,
    disconnect,
    sendTransaction,
    switchChain,
    closeModal,
  };

  return <WalletContext.Provider value={contextValue}>{children}</WalletContext.Provider>;
}
